import { useState, useCallback } from 'react';

interface FormData {
  nombre: string;
  descripcion: string;
}

export interface UseAddMaterialModalReturn {
  formData: FormData;
  loading: boolean;
  error: string | null;
  handleFormChange: (field: string, value: string) => void;
  handleSave: (onSave: (data: FormData) => Promise<void>) => Promise<void>;
  handleClose: (onClose: () => void) => void;
  resetForm: () => void;
}

export const useAddMaterialModal = (): UseAddMaterialModalReturn => {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    descripcion: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFormChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      nombre: '',
      descripcion: '',
    });
    setError(null);
  }, []);

  const handleSave = useCallback(
    async (onSave: (data: FormData) => Promise<void>) => {
      if (!formData.nombre.trim()) {
        setError('El nombre es requerido');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        await onSave(formData);
        resetForm();
      } catch (err: any) {
        setError(err?.body?.message || err?.message || 'Error al crear el material');
      } finally {
        setLoading(false);
      }
    },
    [formData, resetForm],
  );

  const handleClose = useCallback(
    (onClose: () => void) => {
      resetForm();
      onClose();
    },
    [resetForm],
  );

  return {
    formData,
    loading,
    error,
    handleFormChange,
    handleSave,
    handleClose,
    resetForm,
  };
};
